import { Main, Models, DataTable, Pagination, StatusBadge, Modal, PrimaryButton, Input, Assets, EmptyState, Colors, FontFamily } from 'utils/imports.utils';
import { IDataTableColumn } from 'common_components/ui/data_table/data_table.ui';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetState, toastifyError, toastifySuccess } from 'utils/functions.utils';
import { ButtonSize, ButtonTypes } from 'utils/interface.utils';
import { STRINGS } from 'constants/strings.constant';
import { SupportTicketRow, SupportTicketDetailData } from 'interfaces/support.interface';
import FileUpload from 'common_components/ui/file_upload/flie_upload.ui';
import SelectDropdown from 'common_components/ui/select_dropdown/select_dropdown.ui';
import './support.screen.scss';

const PAGE_SIZE = 20;

const text = STRINGS.supportScreen;

const STATUS_FILTER_OPTIONS = [
  { label: text.statusAll, value: '' },
  { label: text.statusOpen, value: 'open' },
  { label: text.statusInProgress, value: 'in_progress' },
  { label: text.statusResolved, value: 'resolved' },
  { label: text.statusClosed, value: 'closed' },
];

const MODAL_STYLE = { borderRadius: '16px', maxWidth: '92vw' };
const BUTTON_STYLE = { borderRadius: '999px' };
const MODAL_BUTTON_STYLE = { ...BUTTON_STYLE, height: '48px' };
const OUTLINE_BUTTON_STYLE = { ...MODAL_BUTTON_STYLE, border: `1.5px solid ${Colors.ink}`, color: Colors.ink, backgroundColor: Colors.white };

const Support = () => {
  const navigate = useNavigate();
  const hasLoadedOnce = useRef(false);
  const [state, setState] = useSetState({
    tickets: [] as SupportTicketRow[],
    totalDocs: 0,
    page: 1,
    loading: true,
    search: '',
    statusFilter: null as { label: string; value: string } | null,
    showCreateModal: false,
    subject: '',
    description: '',
    attachment: null as File | null,
    attachmentPreview: null as string | null,
    submitting: false,
    errorMessage: '',
    selectedTicketId: null as string | null,
    ticketDetail: null as SupportTicketDetailData | null,
    detailLoading: false,
    attachmentUrls: [] as string[],
  });

  const fetchTickets = async (page: number, status?: string) => {
    if (!hasLoadedOnce.current) {
      setState({ loading: true });
    }
    try {
      const response: any = await Models.auth.listSupportTickets({
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        ...(status ? { status } : {}),
      });
      setState({
        tickets: response?.data?.docs || [],
        totalDocs: response?.data?.totalDocs || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to fetch support tickets', err);
      setState({ loading: false });
    } finally {
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    if (token) {
      fetchTickets(state.page, state.statusFilter?.value);
    } else {
      navigate('/login');
    }
  }, [state.page, state.statusFilter]);

  const filteredTickets = state.tickets.filter((ticket) =>
    (ticket.subject || '').toLowerCase().includes(state.search.toLowerCase()),
  );

  const handleCreateTicket = async () => {
    if (!state.subject.trim() || !state.description.trim()) {
      setState({ errorMessage: text.requiredError });
      return;
    }
    setState({ submitting: true, errorMessage: '' });
    try {
      const formData = new FormData();
      formData.append('subject', state.subject.trim());
      formData.append('description', state.description.trim());
      if (state.attachment) {
        formData.append('images', state.attachment);
      }
      await Models.auth.createSupportTicket(formData);
      toastifySuccess(text.submitted);
      clearAttachmentPreview();
      setState({ showCreateModal: false, subject: '', description: '', attachment: null, attachmentPreview: null, page: 1 });
      fetchTickets(1);
    } catch (err) {
      console.error('Failed to create support ticket', err);
      setState({ errorMessage: text.submitFailed });
    } finally {
      setState({ submitting: false });
    }
  };

  const openTicketDetail = async (ticketId: string) => {
    setState({ selectedTicketId: ticketId, ticketDetail: null, detailLoading: true, attachmentUrls: [] });
    try {
      const response: any = await Models.auth.getSupportTicket(ticketId);
      const detail: SupportTicketDetailData | null = response?.data || null;
      setState({ ticketDetail: detail, detailLoading: false });

      const attachments = detail?.ticket?.attachments || [];
      if (attachments.length) {
        const blobs = await Promise.all(
          attachments.map((filename) =>
            (Models.auth.getSupportAttachment(filename, ticketId) as Promise<Blob>).catch((err) => {
              console.error('Failed to fetch support ticket attachment', err);
              return null;
            }),
          ),
        );
        const urls = blobs.filter((blob): blob is Blob => !!blob).map((blob) => URL.createObjectURL(blob));
        setState({ attachmentUrls: urls });
      }
    } catch (err) {
      console.error('Failed to fetch support ticket', err);
      toastifyError(text.loadFailed);
      setState({ detailLoading: false });
    }
  };

  const closeTicketDetail = () => {
    state.attachmentUrls.forEach((url) => URL.revokeObjectURL(url));
    setState({ selectedTicketId: null, ticketDetail: null, detailLoading: false, attachmentUrls: [] });
  };

  const clearAttachmentPreview = () => {
    if (state.attachmentPreview) URL.revokeObjectURL(state.attachmentPreview);
  };

  const handleAttachmentUpload = (file: File) => {
    clearAttachmentPreview();
    setState({ attachment: file, attachmentPreview: URL.createObjectURL(file) });
  };

  const handleAttachmentSizeError = () => {
    toastifyError(text.attachmentSizeError);
  };

  const handleAttachmentTypeError = () => {
    toastifyError(text.attachmentTypeError);
  };

  const columns: IDataTableColumn[] = [
    { key: 'ticket_number', label: text.colTicket },
    { key: 'subject', label: text.colSubject },
    {
      key: 'status',
      label: text.colStatus,
      render: (row: SupportTicketRow) =>
        row.status ? <StatusBadge status={row.status} /> : '—',
    },
    {
      key: 'created_at',
      label: text.colOpened,
      render: (row: SupportTicketRow) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString() : '—',
    },
  ];

  const closeCreateModal = () => {
    clearAttachmentPreview();
    setState({ showCreateModal: false, subject: '', description: '', attachment: null, attachmentPreview: null, errorMessage: '' });
  };

  return (
    <Main isRenderOnlyLogo={false} isAdmin>
      <div className="support">
        <button type="button" className="support_back" onClick={() => navigate('/welcome')}>
          <img src={Assets.arrow_left} alt="" />
          {text.back}
        </button>
        <div className="support_container">
          <div className="support_content_container">
            <div className="support_content_title">{text.title}</div>
            <div className="support_content_desc">{text.subtitle}</div>
          </div>
          <div className="support_button_container">
            <PrimaryButton
              text={text.newTicket}
              left_icon={Assets.plus}
              type={ButtonTypes.PRIMARY_GRADIENT}
              size={ButtonSize.LARGE}
              style={BUTTON_STYLE}
              onClick={() => setState({ showCreateModal: true })}
            />
          </div>
        </div>

        <div className="support_actions">
          <div className="searchContainer">
            <Input
              icon={Assets.search}
              iconPosition="start"
              iconSize={20}
              placeholder={text.searchPlaceholder}
              type="text"
              className="searchInput"
              value={state.search}
              containerStyle={{ height: '44px' }}
              onChange={(e) => setState({ search: e.target.value })}
            />
          </div>
          <div className="sortDropdown">
            <SelectDropdown
              data={STATUS_FILTER_OPTIONS}
              value={state.statusFilter}
              width="180px"
              height="44px"
              placeholder={text.statusPlaceholder}
              iconHeight={20}
              iconWidth={20}
              isFilter
              onChange={(selectedOption) => setState({ statusFilter: selectedOption, page: 1 })}
            />
          </div>
        </div>

        <div className="support_table_container">
          <DataTable
            columns={columns}
            rows={filteredTickets}
            rowKey="_id"
            loading={state.loading}
            emptyMessage={text.emptyMessage}
            onRowClick={(row: SupportTicketRow) => openTicketDetail(row._id)}
          />
        </div>
        <div className="support_pagination_container">
          <Pagination
            currentPage={state.page}
            totalPages={Math.ceil(state.totalDocs / PAGE_SIZE)}
            onPageChange={(page) => setState({ page })}
          />
        </div>

        <Modal isOpen={state.showCreateModal} onClose={closeCreateModal} customStyles={MODAL_STYLE}>
          <div className="support_modal">
            <button type="button" className="support_modal_close" aria-label={text.close} onClick={closeCreateModal}>
              <img src={Assets.close} alt="" />
            </button>
            <h3 className="support_modal_title support_modal_title_center">{text.createTitle}</h3>
            <p className="support_modal_subtitle">{text.createSubtitle}</p>
            <Input
              value={state.subject}
              label={text.subjectLabel}
              placeholder={text.subjectPlaceholder}
              onChange={(e) => setState({ subject: e.target.value, errorMessage: '' })}
            />
            <Input
              value={state.description}
              label={text.descriptionLabel}
              placeholder={text.descriptionPlaceholder}
              isTextArea
              onChange={(e) => setState({ description: e.target.value, errorMessage: '' })}
            />
            {state.attachmentPreview && (
              <img src={state.attachmentPreview} alt={text.previewAlt} className="support_modal_preview" />
            )}
            <div className="support_modal_attach">
              <FileUpload
                fileTypes={['JPEG', 'JPG', 'PNG', 'WEBP']}
                maxFileSize={10 * 1024 * 1024}
                handleChange={handleAttachmentUpload}
                handleSizeError={handleAttachmentSizeError}
                handleTypeError={handleAttachmentTypeError}>
                <PrimaryButton
                  onClick={() => {}}
                  text={state.attachment ? state.attachment.name : text.attachmentLabel}
                  type={ButtonTypes.OUTLINED}
                  size={ButtonSize.LARGE}
                  fontFamily={FontFamily.medium}
                  style={OUTLINE_BUTTON_STYLE}
                />
              </FileUpload>
            </div>
            {state.errorMessage && (
              <div className="support_modal_error" role="alert">
                {state.errorMessage}
              </div>
            )}
            <div className="support_modal_footer support_modal_footer_equal">
              <PrimaryButton
                text={text.cancel}
                type={ButtonTypes.OUTLINED}
                size={ButtonSize.LARGE}
                fontFamily={FontFamily.medium}
                style={OUTLINE_BUTTON_STYLE}
                onClick={closeCreateModal}
              />
              <PrimaryButton
                text={text.submit}
                type={ButtonTypes.PRIMARY_GRADIENT}
                size={ButtonSize.LARGE}
                fontFamily={FontFamily.medium}
                style={MODAL_BUTTON_STYLE}
                activity={state.submitting}
                onClick={handleCreateTicket}
              />
            </div>
          </div>
        </Modal>

        <Modal isOpen={!!state.selectedTicketId} onClose={closeTicketDetail} customStyles={MODAL_STYLE}>
          <div className="support_modal">
            <button type="button" className="support_modal_close" aria-label={text.close} onClick={closeTicketDetail}>
              <img src={Assets.close} alt="" />
            </button>
            {state.detailLoading || !state.ticketDetail ? (
              <div className="support_modal_loading">{text.loading}</div>
            ) : (
              <>
                <div className="support_modal_meta">
                  <span className="support_modal_number">{state.ticketDetail.ticket.ticket_number}</span>
                  {state.ticketDetail.ticket.status && (
                    <StatusBadge status={state.ticketDetail.ticket.status} />
                  )}
                </div>
                <h3 className="support_modal_title support_modal_title_left">{state.ticketDetail.ticket.subject}</h3>
                <p className="support_modal_description">{state.ticketDetail.ticket.description}</p>

                {!!state.ticketDetail.ticket.attachments?.length && (
                  <div className="support_modal_attachments">
                    {state.attachmentUrls.length ? (
                      state.attachmentUrls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt={text.attachmentAlt} className="support_modal_attachment_img" />
                        </a>
                      ))
                    ) : (
                      <div className="support_modal_attachments_loading">{text.attachmentLoading}</div>
                    )}
                  </div>
                )}

                <h4 className="support_modal_thread_title">{text.replies}</h4>
                {state.ticketDetail.replies.length === 0 ? (
                  <EmptyState message={text.noReplies} />
                ) : (
                  <div className="support_modal_thread">
                    {state.ticketDetail.replies.map((reply) => (
                      <div
                        key={reply._id}
                        className={`support_modal_reply ${reply.author_role === 'admin' ? '' : 'support_modal_reply_you'}`}>
                        <span className="support_modal_reply_author">
                          {reply.author_role === 'admin' ? text.replySupport : text.replyYou}
                        </span>
                        <p className="support_modal_reply_message">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      </div>
    </Main>
  );
};

export default Support;
